import type { FinanceRequest } from './purchasePlanning';
import { readyDrawdown } from './loanDrawdown';
export type SpecialRepayments = { payments: { month: number | null; amount: number | null }[]; contractConfirmed: boolean };
export const emptySpecialRepayments = (): SpecialRepayments => ({ payments: [{ month: null, amount: null }], contractConfirmed: false });
const exact = (v: unknown, keys: string[]): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)
  && Object.keys(v).sort().join(',') === [...keys].sort().join(',');
const month = (v: unknown) => v === null || typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 359;
const money = (v: unknown) => v === null || typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100000000 && v === Math.round(v * 100) / 100;
export function validSpecialRepayments(v: unknown): v is SpecialRepayments {
  return exact(v, ['payments', 'contractConfirmed']) && typeof v.contractConfirmed === 'boolean'
    && Array.isArray(v.payments) && v.payments.length <= 60 && v.payments.every(p => exact(p, ['month', 'amount']) && month(p.month) && money(p.amount));
}
/** Explicit month-end payments after the regular instalment. Never pay undrawn
 * principal or turn an unknown contract/schedule into a zero-payment forecast. */
export function readySpecialRepayments(request: Pick<FinanceRequest, 'specialRepayments' | 'loanAmount' | 'drawdown' | 'fixedYears'>) {
  const s = request.specialRepayments;
  if (!validSpecialRepayments(s) || !s.contractConfirmed || !s.payments.length || request.loanAmount === null
    || !money(request.loanAmount) || s.payments.some(p => p.month === null || p.amount === null)) return null;
  const draw = request.drawdown === undefined ? null : readyDrawdown(request);
  if (request.drawdown !== undefined && !draw || s.payments.some(p => p.month! < (draw?.repaymentStartMonth ?? 0))) return null;
  const cents = Array<number>(360).fill(0);
  for (const p of s.payments) cents[p.month!] += Math.round(p.amount! * 100);
  return cents;
}
